import { type FC, useEffect } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import ReactModal from "react-modal";
import { ButtonUI } from "../../../../../components/Button";
import { FormGroupUI } from "../../../../../components/FormGroup";
import { FormGroupLabelUI } from "../../../../../components/FormGroupLabel";
import {
  type Organization,
  useUpdateOrganizationRestApi,
} from "../../../../../hooks/useOrganizationsRestApi";

interface EditOrganizationModalProps {
  isOpen: boolean;
  organization: Organization;
  onClose: () => void;
  onSuccess: () => void;
}

interface FormData {
  name: string;
  description: string;
  isActive: boolean;
}

export const EditOrganizationModal: FC<EditOrganizationModalProps> = ({
  isOpen,
  organization,
  onClose,
  onSuccess,
}) => {
  const { updateOrganization, loading: isSubmitting } =
    useUpdateOrganizationRestApi();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>();

  // モーダルが開いた時に初期値を設定
  useEffect(() => {
    if (isOpen && organization) {
      reset({
        name: organization.name,
        description: organization.description || "",
        isActive: organization.isActive,
      });
    }
  }, [isOpen, organization, reset]);

  const onSubmit: SubmitHandler<FormData> = async data => {
    try {
      await updateOrganization(organization.id, {
        name: data.name.trim(),
        description: data.description.trim() || undefined,
        isActive: data.isActive,
      });

      onSuccess();
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      alert(`更新に失敗しました: ${errorMessage}`);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      reset();
      onClose();
    }
  };

  const modalStyle = {
    content: {
      top: "50%",
      left: "50%",
      right: "auto",
      bottom: "auto",
      marginRight: "-50%",
      transform: "translate(-50%, -50%)",
      width: "90%",
      maxWidth: "500px",
      maxHeight: "80vh",
      overflow: "auto",
      borderRadius: "8px",
      border: "none",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    },
    overlay: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      zIndex: 1000,
    },
  };

  return (
    <ReactModal
      isOpen={isOpen}
      onRequestClose={handleClose}
      style={modalStyle}
      contentLabel="組織編集"
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">組織編集</h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">
                編集対象組織
              </h3>
              <div className="mt-1 text-sm text-blue-700">
                <p>ID: {organization.id}</p>
                <p>現在の名前: {organization.name}</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <FormGroupUI>
            <FormGroupLabelUI>組織名 *</FormGroupLabelUI>
            <input
              {...register("name", {
                required: "組織名は必須です",
                minLength: {
                  value: 2,
                  message: "組織名は2文字以上で入力してください",
                },
                maxLength: {
                  value: 100,
                  message: "組織名は100文字以下で入力してください",
                },
              })}
              type="text"
              placeholder="組織名を入力"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.name ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </FormGroupUI>

          <FormGroupUI>
            <FormGroupLabelUI>説明</FormGroupLabelUI>
            <textarea
              {...register("description", {
                maxLength: {
                  value: 500,
                  message: "説明は500文字以下で入力してください",
                },
              })}
              placeholder="組織の説明を入力（任意）"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.description ? "border-red-300" : "border-gray-300"
              }`}
              disabled={isSubmitting}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">
                {errors.description.message}
              </p>
            )}
          </FormGroupUI>

          <FormGroupUI>
            <div className="flex items-center">
              <input
                {...register("isActive")}
                type="checkbox"
                id="isActive"
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                disabled={isSubmitting}
              />
              <label
                htmlFor="isActive"
                className="ml-2 block text-sm text-gray-900"
              >
                アクティブ状態
              </label>
            </div>
            <p className="mt-1 text-sm text-gray-500">
              チェックを外すと組織が非アクティブ状態になります
            </p>
          </FormGroupUI>

          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <ButtonUI
              type="button"
              onClick={handleClose}
              disabled={isSubmitting}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              キャンセル
            </ButtonUI>
            <ButtonUI
              type="submit"
              disabled={isSubmitting || !isDirty}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
            >
              {isSubmitting ? "更新中..." : "更新"}
            </ButtonUI>
          </div>
        </form>
      </div>
    </ReactModal>
  );
};
